/**
 * 운영자 시설관리자 대신 처리 — 상태·판정 함수 (React 비의존, 단위 테스트 대상).
 * 영구지침: docs/develop/facility/admin-act-as.md §6
 */
import { safeSessionStorage } from '@/lib/safeStorage'

/** 백엔드 ActAsPolicy.HEADER 와 같은 값 */
export const ACT_AS_HEADER = 'X-Act-As-Facility-Manager'

/** sessionStorage 키 (탭 단위 — 브라우저를 닫으면 사라진다) */
export const ACT_AS_STORAGE_KEY = 'cb_act_as_facility_manager'

export interface ActAsTarget {
  /** facility_managers.id (= FacilityManagerResponse.managerId) */
  managerId: number
  facilityName: string
}

/** 헤더를 붙일 API 경로 (apiClient baseURL `/api/v1` 기준). 이 경로 자체·하위·쿼리. §6.2-3 */
const ACT_AS_API_BASES = ['/facility-manager', '/facility/coupons', '/notifications']

/** 대신 처리 중 숨기고 직접 진입도 막는 시설관리자 화면 경로. §6.4 */
export const ACT_AS_BLOCKED_ROUTES = ['/facility/link', '/facility/care-logs', '/facility/inquiry']

export function getActAsTarget(): ActAsTarget | null {
  const raw = safeSessionStorage.getItem(ACT_AS_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { managerId, facilityName } = parsed as Record<string, unknown>
    if (typeof managerId !== 'number' || !Number.isInteger(managerId) || managerId <= 0) return null
    return { managerId, facilityName: typeof facilityName === 'string' ? facilityName : '' }
  } catch {
    return null
  }
}

export function startActAs(target: ActAsTarget): void {
  safeSessionStorage.setItem(ACT_AS_STORAGE_KEY, JSON.stringify(target))
}

export function clearActAs(): void {
  safeSessionStorage.removeItem(ACT_AS_STORAGE_KEY)
}

export function isOperatorRole(role: string | null | undefined): boolean {
  return role === 'ADMIN' || role === 'SUPER_ADMIN'
}

function isFacilityRoute(pathname: string): boolean {
  return pathname === '/facility' || pathname.startsWith('/facility/')
}

/** baseURL 이 붙기 전 상대 경로 또는 절대 URL 에서 `/api/v1` 뒤 경로를 꺼낸다. */
function apiPath(url: string): string {
  let path = url
  const schemeIdx = path.indexOf('://')
  if (schemeIdx >= 0) {
    const slash = path.indexOf('/', schemeIdx + 3)
    path = slash >= 0 ? path.slice(slash) : '/'
  }
  if (path.startsWith('/api/v1/')) path = path.slice('/api/v1'.length)
  return path
}

/**
 * 요청에 대신 처리 헤더를 붙일지. §6.2
 * 0) 로그인 사용자가 운영자이고 1) 대상이 저장돼 있고 2) 현재 화면이 /facility/* 이며
 * 3) URL 이 대신 처리 범위 경로일 때만. (시설관리자 본인 요청에는 절대 붙지 않는다)
 */
export function shouldAttachActAsHeader(
  url: string | undefined,
  pathname: string,
  target: ActAsTarget | null,
  role: string | null | undefined,
): boolean {
  if (!isOperatorRole(role) || !target || !url) return false
  if (!isFacilityRoute(pathname)) return false
  const path = apiPath(url)
  return ACT_AS_API_BASES.some(
    (base) => path === base || path.startsWith(base + '/') || path.startsWith(base + '?'),
  )
}

/** 대신 처리 중 접근 차단 화면인가. §6.4 */
export function isActAsBlockedRoute(pathname: string): boolean {
  return ACT_AS_BLOCKED_ROUTES.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

export type FacilityAccess =
  | { kind: 'wait' }
  | { kind: 'allow'; actingAs: ActAsTarget | null }
  | { kind: 'redirect'; to: string }

/** RequireFacility 분기. §6.3 */
export function decideFacilityAccess(
  auth: { hydrated: boolean; isAuthenticated: boolean; role: string | null | undefined },
  target: ActAsTarget | null,
): FacilityAccess {
  if (!auth.hydrated) return { kind: 'wait' }
  if (!auth.isAuthenticated) return { kind: 'redirect', to: '/facility/login' }
  if (auth.role === 'FACILITY') return { kind: 'allow', actingAs: null }
  if (isOperatorRole(auth.role)) {
    return target
      ? { kind: 'allow', actingAs: target }
      : { kind: 'redirect', to: '/admin/facility-managers' }
  }
  return { kind: 'redirect', to: '/facility/login' }
}
